<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import schedulingAPI from '../api/scheduling';
import SchedulingDropdown from './SchedulingDropdown.vue';

const props = defineProps({
  services: {
    type: Array,
    default: () => [],
  },
  professionals: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['open-enrollment-modal', 'open-enrollment-details']);

const isLoading = ref(true);
const selectedPeriod = ref('morning'); // 'morning' | 'afternoon' | 'night' | 'all'
const selectedProfessionalId = ref('');
const selectedServiceId = ref('');

// Slot Overview Modal State (para detalhar os alunos do horário)
const showSlotStudentsModal = ref(false);
const selectedSlotForModal = ref(null);

// Pending Presences Modal State (para listar e resolver pendências anteriores)
const showPendingPresencesModal = ref(false);
const globalPastPendingCount = ref(0);
const globalPastPendingList = ref([]);
const isResolvingPending = ref(false);

const filteredProfessionals = computed(() => {
  if (!selectedServiceId.value) return props.professionals;
  const sId = Number(selectedServiceId.value);
  return props.professionals.filter(p => {
    if (!p.service_ids || p.service_ids.length === 0) return true;
    return p.service_ids.map(Number).includes(sId);
  });
});

const filteredServices = computed(() => {
  if (!selectedProfessionalId.value) return props.services;
  const prof = props.professionals.find(p => Number(p.id) === Number(selectedProfessionalId.value));
  if (!prof || !prof.service_ids || prof.service_ids.length === 0) return props.services;
  const profServiceIds = prof.service_ids.map(Number);
  return props.services.filter(s => profServiceIds.includes(Number(s.id)));
});

const professionalOptions = computed(() => [
  { value: '', label: 'Todos os Profissionais' },
  ...filteredProfessionals.value.map(p => ({
    value: p.id,
    label: `${p.name} (${p.specialty || 'Geral'})`,
  })),
]);

const serviceOptions = computed(() => [
  { value: '', label: 'Todos os Serviços' },
  ...filteredServices.value.map(s => ({
    value: s.id,
    label: s.name,
    color: s.color,
  })),
]);

const isFiltered = computed(() => {
  return Boolean(selectedProfessionalId.value || selectedServiceId.value);
});

const onProfessionalChange = () => {
  if (selectedServiceId.value) {
    const validServiceIds = filteredServices.value.map(s => String(s.id));
    if (!validServiceIds.includes(String(selectedServiceId.value))) {
      selectedServiceId.value = '';
    }
  }
};

const onServiceChange = () => {
  if (selectedProfessionalId.value) {
    const validProfIds = filteredProfessionals.value.map(p => String(p.id));
    if (!validProfIds.includes(String(selectedProfessionalId.value))) {
      selectedProfessionalId.value = '';
    }
  }
};

const daysOfWeek = [
  { key: 'monday', label: 'SEG', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'TER', full: 'Terça-feira' },
  { key: 'wednesday', label: 'QUA', full: 'Quarta-feira' },
  { key: 'thursday', label: 'QUI', full: 'Quinta-feira' },
  { key: 'friday', label: 'SEX', full: 'Sexta-feira' },
  { key: 'saturday', label: 'SÁB', full: 'Sábado' },
];

const todayDayKey = computed(() => {
  const dayIndex = new Date().getDay();
  const map = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };
  return map[dayIndex] || null;
});

const morningSlots = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonSlots = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightSlots = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const timeSlots = computed(() => {
  if (selectedPeriod.value === 'morning') return morningSlots;
  if (selectedPeriod.value === 'afternoon') return afternoonSlots;
  if (selectedPeriod.value === 'night') return nightSlots;
  return [...morningSlots, ...afternoonSlots, ...nightSlots];
});

const gridMatrix = ref({});
const defaultCapacity = ref(1);

const fetchGridData = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (selectedProfessionalId.value) params.professional_id = selectedProfessionalId.value;
    if (selectedServiceId.value) params.service_id = selectedServiceId.value;

    const res = await schedulingAPI.getFixedGrid(params);
    gridMatrix.value = res.data.grid || {};
    defaultCapacity.value = res.data.default_capacity || 1;
    globalPastPendingCount.value = res.data.global_past_pending_count || 0;
    globalPastPendingList.value = res.data.global_past_pending_list || [];
  } catch (err) {
    console.error('Erro ao carregar grade fixa:', err);
  } finally {
    isLoading.value = false;
  }
};

const getSlotData = (dayKey, slotTime) => {
  return gridMatrix.value[dayKey]?.[slotTime] || {
    day_key: dayKey,
    time_slot: slotTime,
    capacity: defaultCapacity.value,
    occupants_count: 0,
    free_slots_count: defaultCapacity.value,
    is_full: false,
    enrollments: [],
    reschedulings: [],
    by_service: [],
    has_past_pending: false,
  };
};

const handleSlotClick = (dayKey, slotTime) => {
  const slot = getSlotData(dayKey, slotTime);

  // Se tiver alunos matriculados, abre a lista detalhada
  if (slot.occupants_count > 0) {
    const dayItem = daysOfWeek.find(d => d.key === dayKey);
    openSlotOverview(dayItem, slotTime);
    return;
  }

  // Se o slot estiver vazio, abre diretamente o modal de nova matrícula
  emit('open-enrollment-modal', {
    day_key: dayKey,
    time_slot: slotTime,
    professional_id: selectedProfessionalId.value || null,
    service_id: selectedServiceId.value || null,
  });
};

const openEnrollmentCard = (enrollment, dayItem, slotTime) => {
  emit('open-enrollment-details', {
    enrollmentId: enrollment.id,
    appointmentId: enrollment.appointment_id || enrollment.past_pending_appointment_id,
    dayKey: dayItem?.key,
    dayLabel: dayItem?.full,
    timeSlot: slotTime,
    currentWeekAttended: enrollment.current_week_attended,
    has_past_pending: enrollment.has_past_pending,
    past_pending_appointment_id: enrollment.past_pending_appointment_id,
    past_pending_date: enrollment.past_pending_date,
    past_pending_time: enrollment.past_pending_time,
  });
};

const openSlotOverview = (dayItem, timeSlot) => {
  const slotData = getSlotData(dayItem.key, timeSlot);
  selectedSlotForModal.value = {
    dayItem,
    timeSlot,
    slotData,
  };
  showSlotStudentsModal.value = true;
};

const openEnrollmentFromModal = enrollment => {
  showSlotStudentsModal.value = false;
  if (!selectedSlotForModal.value) return;
  openEnrollmentCard(
    enrollment,
    selectedSlotForModal.value.dayItem,
    selectedSlotForModal.value.timeSlot
  );
};

const createEnrollmentFromModal = serviceId => {
  const { dayItem, timeSlot } = selectedSlotForModal.value;
  showSlotStudentsModal.value = false;
  emit('open-enrollment-modal', {
    day_key: dayItem.key,
    time_slot: timeSlot,
    professional_id: selectedProfessionalId.value || null,
    service_id: serviceId || selectedServiceId.value || null,
  });
};

const activeRescheduleItemId = ref(null);
const rescheduleForm = ref({
  targetDate: new Date().toISOString().split('T')[0],
  targetTime: '08:00',
  professionalId: null,
  serviceId: null,
});

// Estado do Mini Calendário com Heatmap de Vagas
const calYear = ref(new Date().getFullYear());
const calMonth = ref(new Date().getMonth() + 1);
const monthOverviewData = ref(null);
const isLoadingMonthOverview = ref(false);

const currentMonthLabel = computed(() => {
  const d = new Date(calYear.value, calMonth.value - 1, 1);
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(d).toUpperCase();
});

const loadMonthOverview = async () => {
  if (!rescheduleForm.value.professionalId) return;
  isLoadingMonthOverview.value = true;
  try {
    const res = await schedulingAPI.getMonthOverview({
      year: calYear.value,
      month: calMonth.value,
      professional_id: rescheduleForm.value.professionalId,
      service_id: rescheduleForm.value.serviceId || null,
    });
    monthOverviewData.value = res.data;
  } catch (err) {
    console.error('Erro ao carregar mapa de vagas do mês:', err);
  } finally {
    isLoadingMonthOverview.value = false;
  }
};

const prevMonth = () => {
  if (calMonth.value === 1) {
    calMonth.value = 12;
    calYear.value -= 1;
  } else {
    calMonth.value -= 1;
  }
  loadMonthOverview();
};

const nextMonth = () => {
  if (calMonth.value === 12) {
    calMonth.value = 1;
    calYear.value += 1;
  } else {
    calMonth.value += 1;
  }
  loadMonthOverview();
};

const calendarDaysGrid = computed(() => {
  const year = calYear.value;
  const month = calMonth.value;
  const firstDayOfMonth = new Date(year, month - 1, 1);
  const lastDayOfMonth = new Date(year, month, 0);
  const totalDays = lastDayOfMonth.getDate();

  // Começa na Segunda-feira (índice 0 = Seg ... 6 = Dom)
  let startOffset = firstDayOfMonth.getDay() - 1;
  if (startOffset === -1) startOffset = 6;

  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push({ isBlank: true });
  }

  const todayStr = new Date().toISOString().split('T')[0];

  for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
    const dStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    const dayInfo = monthOverviewData.value?.days?.[dStr] || { status: 'available', available_slots: 0, total_slots: 0 };
    const isSelected = rescheduleForm.value.targetDate === dStr;
    const isPast = dStr < todayStr;
    const isOff = dayInfo.status === 'off';
    const isFull = dayInfo.status === 'full';
    const isBlocked = dayInfo.status === 'blocked';
    const isClickable = !isPast && !isOff && !isFull && !isBlocked;

    cells.push({
      dayNum,
      dateString: dStr,
      status: isPast ? 'past' : dayInfo.status,
      availableSlots: dayInfo.available_slots || 0,
      totalSlots: dayInfo.total_slots || 0,
      isSelected,
      isPast,
      isOff,
      isFull,
      isBlocked,
      isClickable,
    });
  }

  return cells;
});

const selectedDaySlots = computed(() => {
  const dStr = rescheduleForm.value.targetDate;
  if (!dStr) return [];
  return monthOverviewData.value?.day_slots?.[dStr] || [];
});

const onSelectCalendarDay = (dayCell) => {
  if (!dayCell.isClickable) return;
  rescheduleForm.value.targetDate = dayCell.dateString;
  const slots = monthOverviewData.value?.day_slots?.[dayCell.dateString] || [];
  const currentSlotValid = slots.find(s => s.time_label === rescheduleForm.value.targetTime && s.available);
  if (!currentSlotValid) {
    const firstFree = slots.find(s => s.available);
    if (firstFree) {
      rescheduleForm.value.targetTime = firstFree.time_label;
    }
  }
};

const groupedPastPendingList = computed(() => {
  const groups = {};
  const list = [...(globalPastPendingList.value || [])];

  list.sort((a, b) => {
    const parseDate = (dStr, tStr) => {
      const [d, m, y] = (dStr || '').split('/');
      return new Date(`${y}-${m}-${d}T${tStr || '00:00'}:00`).getTime() || 0;
    };
    return parseDate(a.date, a.time) - parseDate(b.date, b.time);
  });

  list.forEach(item => {
    const key = item.date || 'Sem data';
    if (!groups[key]) {
      groups[key] = {
        date: item.date,
        dayOfWeek: item.day_of_week,
        items: [],
      };
    }
    groups[key].items.push(item);
  });

  return Object.values(groups);
});

const quickResolvePastPending = async (pendingItem) => {
  if (!pendingItem?.id) return;
  isResolvingPending.value = true;
  try {
    await schedulingAPI.updateAppointmentStatus(pendingItem.id, 'completed');
    await fetchGridData();
  } catch (err) {
    console.error('Erro ao resolver presença pendente:', err);
  } finally {
    isResolvingPending.value = false;
  }
};

const markPendingAbsent = async (pendingItem) => {
  if (!pendingItem?.id) return;
  isResolvingPending.value = true;
  try {
    await schedulingAPI.updateAppointmentStatus(pendingItem.id, 'cancelled');
    await fetchGridData();
  } catch (err) {
    console.error('Erro ao registrar falta:', err);
  } finally {
    isResolvingPending.value = false;
  }
};

const showRescheduleModal = ref(false);
const selectedPendingForReschedule = ref(null);

const openRescheduleModal = (item) => {
  selectedPendingForReschedule.value = item;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  calYear.value = today.getFullYear();
  calMonth.value = today.getMonth() + 1;
  rescheduleForm.value = {
    targetDate: `${y}-${m}-${d}`,
    targetTime: item.time || '08:00',
    professionalId: item.professional_id || props.professionals[0]?.id || null,
    serviceId: item.service_id || null,
  };
  showRescheduleModal.value = true;
  loadMonthOverview();
};

const closeRescheduleModal = () => {
  showRescheduleModal.value = false;
  selectedPendingForReschedule.value = null;
};

const confirmReschedulePending = async (item) => {
  const targetItem = item || selectedPendingForReschedule.value;
  if (!targetItem?.id) return;
  isResolvingPending.value = true;
  try {
    const startDateTime = `${rescheduleForm.value.targetDate}T${rescheduleForm.value.targetTime}:00`;
    const payload = {
      start_time: startDateTime,
      status: 'confirmed',
    };
    if (rescheduleForm.value.professionalId) {
      payload.scheduling_professional_id = rescheduleForm.value.professionalId;
    }
    await schedulingAPI.updateAppointment(targetItem.id, payload);
    closeRescheduleModal();
    await fetchGridData();
  } catch (err) {
    console.error('Erro ao reagendar atendimento pendente:', err);
  } finally {
    isResolvingPending.value = false;
  }
};

const confirmAllPastPending = async () => {
  if (!globalPastPendingList.value.length) return;
  isResolvingPending.value = true;
  try {
    const promises = globalPastPendingList.value.map(item =>
      schedulingAPI.updateAppointmentStatus(item.id, 'completed')
    );
    await Promise.all(promises);
    await fetchGridData();
    showPendingPresencesModal.value = false;
  } catch (err) {
    console.error('Erro ao confirmar todas as pendências:', err);
  } finally {
    isResolvingPending.value = false;
  }
};

watch([selectedProfessionalId, selectedServiceId], () => {
  fetchGridData();
});

onMounted(() => {
  fetchGridData();
});

defineExpose({
  fetchGridData,
});
</script>

<template>
  <div class="space-y-4">
    <!-- Top Filter Bar (Flat & Seamless Modern Layout) -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 select-none pb-1">
      <!-- Shifts Selector (Chatwoot v4 Segmented Tab Style) -->
      <div class="flex items-center gap-0.5 p-0.5 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak/60 rounded-lg overflow-x-auto max-w-full">
        <button
          type="button"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
          :class="selectedPeriod === 'morning' ? 'bg-n-solid-active shadow-xs text-n-slate-12 font-semibold' : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'"
          @click="selectedPeriod = 'morning'"
        >
          <span>Manhã</span>
        </button>
        <button
          type="button"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
          :class="selectedPeriod === 'afternoon' ? 'bg-n-solid-active shadow-xs text-n-slate-12 font-semibold' : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'"
          @click="selectedPeriod = 'afternoon'"
        >
          <span>Tarde</span>
        </button>
        <button
          type="button"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
          :class="selectedPeriod === 'night' ? 'bg-n-solid-active shadow-xs text-n-slate-12 font-semibold' : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'"
          @click="selectedPeriod = 'night'"
        >
          <span>Noite</span>
        </button>
        <button
          type="button"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
          :class="selectedPeriod === 'all' ? 'bg-n-solid-active shadow-xs text-n-slate-12 font-semibold' : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'"
          @click="selectedPeriod = 'all'"
        >
          <span>Dia Todo</span>
        </button>
      </div>

      <!-- Filters & Action Button -->
      <div class="flex items-center gap-2">
        <!-- Professional Filter Dropdown -->
        <SchedulingDropdown
          v-model="selectedProfessionalId"
          :options="professionalOptions"
          placeholder="Todos os Profissionais"
          align="right"
          @change="onProfessionalChange"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </template>
        </SchedulingDropdown>

        <!-- Service Filter Dropdown -->
        <SchedulingDropdown
          v-model="selectedServiceId"
          :options="serviceOptions"
          placeholder="Todos os Serviços"
          align="right"
          @change="onServiceChange"
        >
          <template #icon>
            <svg class="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </template>
        </SchedulingDropdown>

        <!-- Pending Presences Global Button -->
        <button
          v-if="globalPastPendingCount > 0"
          type="button"
          class="h-8 px-3 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 font-medium text-xs rounded-lg shadow-xs transition-all cursor-pointer shrink-0 flex items-center gap-1.5 animate-pulse"
          title="Ver atendimentos anteriores aguardando confirmação de presença"
          @click="showPendingPresencesModal = true"
        >
          <span>⚠️</span>
          <span>{{ globalPastPendingCount }} pendência(s)</span>
        </button>

        <!-- New Enrollment Button -->
        <button
          type="button"
          class="h-8 px-3 bg-brand hover:bg-brand-hover active:bg-brand text-white font-medium text-xs rounded-lg shadow-xs transition-all cursor-pointer shrink-0 flex items-center gap-1.5"
          @click="emit('open-enrollment-modal', null)"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Matricular Aluno</span>
        </button>
      </div>
    </div>

    <!-- Fixed Weekly Grid Table -->
    <div class="overflow-x-auto rounded-xl border border-n-weak bg-n-surface-1 shadow-xs">
      <div class="min-w-[950px]">
        <!-- Days Header -->
        <div class="grid grid-cols-[72px_repeat(6,minmax(0,1fr))] border-b border-n-weak bg-n-surface-1 sticky top-0 z-20">
          <div class="py-2.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-n-slate-10 text-center border-r border-n-weak/60 flex items-center justify-center">
            HORÁRIO
          </div>
          <div
            v-for="d in daysOfWeek"
            :key="d.key"
            class="py-2 px-1 text-center border-r border-n-weak/60 last:border-r-0 transition-colors flex items-center justify-center hover:bg-n-alpha-1/40"
          >
            <div class="flex flex-col items-center justify-center select-none py-1">
              <div class="flex items-center gap-1">
                <span
                  class="text-[11px] font-semibold uppercase tracking-wider leading-none"
                  :class="d.key === todayDayKey ? 'text-n-brand' : 'text-n-slate-11'"
                >
                  {{ d.label }}
                </span>
                <span v-if="d.key === todayDayKey" class="w-1.5 h-1.5 rounded-full bg-brand"></span>
              </div>
              <span
                class="text-xs font-medium text-n-slate-12 mt-1 leading-none"
                :class="d.key === todayDayKey ? 'text-n-brand font-semibold' : 'text-n-slate-11'"
              >
                {{ d.full }}
              </span>
            </div>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div v-if="isLoading" class="flex justify-center items-center py-24 bg-n-surface-1">
          <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
        </div>

        <!-- Grid Slots Rows -->
        <div v-else class="divide-y divide-n-weak/60 bg-n-surface-1">
          <div
            v-for="timeSlot in timeSlots"
            :key="timeSlot"
            class="grid grid-cols-[72px_repeat(6,minmax(0,1fr))] min-h-[88px] transition-colors"
          >
            <!-- Slot Time Label (Clean, Minimalist Mono Font) -->
            <div class="p-2 text-center border-r border-n-weak/60 flex items-start justify-center pt-3 select-none bg-n-alpha-1/10">
              <span class="text-xs font-mono text-n-slate-10 font-medium">
                {{ timeSlot }}
              </span>
            </div>

            <!-- Day Columns -->
            <div
              v-for="d in daysOfWeek"
              :key="`${d.key}-${timeSlot}`"
              class="p-1.5 border-r border-n-weak/60 last:border-r-0 relative group transition-colors flex flex-col justify-center"
              :class="[
                d.key === todayDayKey ? 'bg-n-alpha-1/25' : '',
                getSlotData(d.key, timeSlot).occupants_count === 0
                  ? 'hover:bg-n-alpha-1/50 cursor-pointer'
                  : ''
              ]"
              @click="handleSlotClick(d.key, timeSlot)"
            >
              <!-- MODO 1: FILTRADO (Profissional ou Serviço selecionado) -> CARDS INDIVIDUAIS DIRETOS -->
              <template v-if="isFiltered">
                <div v-if="getSlotData(d.key, timeSlot).occupants_count > 0" class="space-y-1.5 w-full">
                  <!-- Card individual de cada matrícula / paciente -->
                  <div
                    v-for="enrollment in getSlotData(d.key, timeSlot).enrollments"
                    :key="enrollment.id"
                    class="p-2 rounded-lg text-xs shadow-xs border transition-all cursor-pointer relative overflow-hidden bg-n-surface-1 border-n-weak hover:border-brand/40 group/card"
                    :class="enrollment.has_past_pending ? 'border-amber-500/50 ring-1 ring-amber-500/20' : ''"
                    @click.stop="openEnrollmentCard(enrollment, d, timeSlot)"
                  >
                    <!-- Faixa de cor do serviço -->
                    <div
                      class="absolute left-0 top-0 bottom-0 w-[3px] rounded-l"
                      :style="{ backgroundColor: enrollment.service?.color || 'var(--brand-color, #2781F6)' }"
                    />
                    <div class="pl-1 flex flex-col gap-1">
                      <div class="flex items-center justify-between gap-1">
                        <span class="font-semibold text-n-slate-12 text-[11px] truncate max-w-[110px]" :title="enrollment.patient?.name">
                          {{ enrollment.patient?.name || 'Sem nome' }}
                        </span>
                        <!-- Selo de Pendência vs Tipo / Sessões Concluídas -->
                        <span
                          v-if="enrollment.has_past_pending"
                          class="text-[8px] px-1.5 py-0.2 rounded font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0 flex items-center gap-0.5"
                          title="Presença anterior pendente de confirmação"
                        >
                          <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          <span>Pendente</span>
                        </span>
                        <span
                          v-else-if="enrollment.session_type === 'trial'"
                          class="text-[9px] px-1.5 py-0.2 rounded font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0"
                          title="Aula Experimental"
                        >
                          Exp
                        </span>
                        <span
                          v-else
                          class="text-[9px] px-1.5 py-0.2 rounded font-mono font-medium bg-n-alpha-2 text-n-slate-11 border border-n-weak/60 shrink-0"
                        >
                          {{ enrollment.completed_sessions }}/{{ enrollment.total_sessions }}
                        </span>
                      </div>
                      <div class="text-[10px] text-n-slate-11 truncate flex items-center justify-between">
                        <span class="truncate max-w-[90px] flex items-center gap-1">
                          <span v-if="enrollment.service?.attendance_type === 'individual'" class="text-n-brand text-[9px]">👤</span>
                          <span v-else class="text-emerald-500 dark:text-emerald-400 text-[9px]">👥</span>
                          <span>{{ enrollment.service?.name }}</span>
                        </span>
                        <span v-if="!selectedProfessionalId" class="text-n-slate-10 text-[9px] truncate max-w-[70px]">
                          {{ enrollment.professional?.name }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Reposições / Encaixes -->
                  <div
                    v-for="reschedule in getSlotData(d.key, timeSlot).reschedulings"
                    :key="`reschedule-${reschedule.id}`"
                    class="p-1.5 rounded-lg text-[11px] shadow-xs border border-indigo-500/20 bg-indigo-500/5 text-indigo-600 dark:text-indigo-300 cursor-pointer hover:border-indigo-400/40"
                    @click.stop="openEnrollmentCard({ id: reschedule.enrollment_id }, d, timeSlot)"
                  >
                    <div class="flex items-center justify-between">
                      <span class="font-medium text-indigo-700 dark:text-indigo-200 truncate">{{ reschedule.patient?.name }}</span>
                      <span class="text-[8px] px-1 py-0.2 rounded bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 font-medium">🔄 Reposição</span>
                    </div>
                  </div>

                  <!-- Indicador de Vagas Livres no Horário -->
                  <div
                    v-if="!getSlotData(d.key, timeSlot).is_full"
                    class="py-0.5 text-center text-[10px] font-medium text-n-slate-10 hover:text-n-brand border border-dashed border-n-weak/60 rounded-lg hover:border-brand/50 cursor-pointer transition-colors flex items-center justify-center gap-1"
                    title="Clique para matricular mais um aluno neste horário"
                    @click.stop="handleSlotClick(d.key, timeSlot)"
                  >
                    <span>+</span>
                    <span>{{ getSlotData(d.key, timeSlot).free_slots_count }} vaga(s)</span>
                  </div>
                </div>

                <!-- Horário 100% Vazio no Filtro (Aparece Apenas no Hover) -->
                <div
                  v-else
                  class="h-full min-h-[44px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  @click.stop="handleSlotClick(d.key, timeSlot)"
                >
                  <span class="text-[10px] font-medium text-n-slate-11 group-hover:text-n-brand bg-n-alpha-1 border border-n-weak px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
                    <span>+</span>
                    <span>Matricular</span>
                  </span>
                </div>
              </template>

              <!-- MODO 2: NÃO FILTRADO (Visão Geral de Todos os Profissionais) -> CARD CONSOLIDADO POR VAGAS -->
              <template v-else>
                <div
                  v-if="getSlotData(d.key, timeSlot).occupants_count > 0"
                  class="p-2 rounded-lg bg-n-surface-1 border border-n-weak transition-all cursor-pointer shadow-xs space-y-1.5 w-full hover:border-brand/40"
                  :class="getSlotData(d.key, timeSlot).has_past_pending ? 'border-amber-500/40 hover:border-amber-400' : ''"
                  title="Clique para ver os alunos deste horário"
                  @click.stop="openSlotOverview(d, timeSlot)"
                >
                  <!-- Linhas dos Serviços Ocupados -->
                  <div class="space-y-1">
                    <div
                      v-for="srvGroup in getSlotData(d.key, timeSlot).by_service"
                      :key="srvGroup.service_id"
                      class="flex items-center justify-between text-xs"
                    >
                      <div class="flex items-center gap-1.5 truncate">
                        <span class="w-1.5 h-1.5 rounded-full shrink-0" :style="{ backgroundColor: srvGroup.color || 'var(--brand-color, #2781F6)' }" />
                        <span class="font-medium text-n-slate-12 truncate text-[11px]">{{ srvGroup.service_name }}</span>
                        <span
                          v-if="srvGroup.has_past_pending"
                          class="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0 animate-pulse"
                          title="Existe presença pendente neste horário"
                        />
                      </div>
                      <span
                        class="font-mono text-[10px] font-medium shrink-0 ml-1 px-1.5 py-0.2 rounded flex items-center gap-1"
                        :class="srvGroup.is_full ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20' : 'bg-n-alpha-2 text-n-slate-11'"
                      >
                        <span v-if="srvGroup.attendance_type === 'individual'">1/1</span>
                        <span v-else>{{ srvGroup.occupants_count }}/{{ srvGroup.capacity }}</span>
                      </span>
                    </div>
                  </div>

                  <!-- Rodapé do Card -->
                  <div class="pt-1 border-t border-n-weak/50 text-[10px] text-n-slate-11 font-medium flex items-center justify-between">
                    <span class="text-n-slate-12 font-semibold text-[11px]">{{ getSlotData(d.key, timeSlot).occupants_count }} {{ getSlotData(d.key, timeSlot).occupants_count === 1 ? 'aluno' : 'alunos' }}</span>
                    <span v-if="!getSlotData(d.key, timeSlot).is_full" class="text-n-slate-10 font-normal">+{{ getSlotData(d.key, timeSlot).free_slots_count }} livre(s)</span>
                  </div>
                </div>

                <!-- Horário 100% Vazio na Visão Geral (Aparece Apenas no Hover) -->
                <div
                  v-else
                  class="h-full min-h-[44px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  @click.stop="handleSlotClick(d.key, timeSlot)"
                >
                  <span class="text-[10px] font-medium text-n-slate-11 group-hover:text-n-brand bg-n-alpha-1 border border-n-weak px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
                    <span>+</span>
                    <span>Matricular</span>
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE ALUNOS DO HORÁRIO (COM OS CARDS INDIVIDUAIS COMPLETOS) -->
    <div
      v-if="showSlotStudentsModal && selectedSlotForModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showSlotStudentsModal = false"
    >
      <div class="w-full max-w-lg bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 flex flex-col max-h-[85vh]">
        <!-- Modal Header -->
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
              <span>Atendimentos no Horário</span>
              <span class="text-xs px-2 py-0.5 rounded-md bg-brand-subtle text-n-brand border border-brand/30 font-medium">
                {{ selectedSlotForModal.dayItem.full }} às {{ selectedSlotForModal.timeSlot }}
              </span>
            </h3>
            <p class="text-xs text-n-slate-11 mt-0.5">
              Selecione um aluno para registrar presença/prontuário ou faça uma nova matrícula.
            </p>
          </div>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-base" @click="showSlotStudentsModal = false">✕</button>
        </div>

        <!-- Students List (Cards Individuais Completos) -->
        <div class="p-6 overflow-y-auto space-y-2.5 flex-1">
          <div
            v-for="enrollment in selectedSlotForModal.slotData.enrollments"
            :key="enrollment.id"
            class="p-2.5 rounded-lg text-xs shadow-xs border transition-all cursor-pointer relative overflow-hidden hover:border-brand bg-n-surface-1"
            :class="enrollment.has_past_pending ? 'border-amber-500/60 ring-1 ring-amber-500/30' : 'border-n-weak'"
            @click="openEnrollmentFromModal(enrollment)"
          >
            <!-- Color Accent Bar -->
            <div
              class="absolute left-0 top-0 bottom-0 w-1"
              :style="{ backgroundColor: enrollment.service?.color || 'var(--brand-color, #1f93ff)' }"
            />

            <div class="pl-2 flex items-center justify-between">
              <div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-n-slate-12 text-xs">
                    {{ enrollment.patient?.name || 'Sem nome' }}
                  </span>
                  <span class="text-[10px] text-n-slate-11">
                    ({{ enrollment.service?.name }} • Prof: {{ enrollment.professional?.name || 'Geral' }})
                  </span>
                </div>
                <div class="text-[10px] mt-0.5">
                  <span v-if="enrollment.has_past_pending" class="text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
                    <span>⚠️</span> Atendimento anterior pendente de confirmação
                  </span>
                  <span v-else-if="enrollment.current_week_attended" class="text-emerald-600 dark:text-emerald-400 font-semibold">
                    ✓ Presença confirmada nesta semana
                  </span>
                  <span v-else class="text-n-slate-11">
                    Sessão em aberto (clique para registrar)
                  </span>
                </div>
              </div>

              <!-- Status Badge -->
              <div class="flex items-center gap-1.5 shrink-0">
                <span
                  v-if="enrollment.has_past_pending"
                  class="text-[9px] px-2 py-0.5 rounded-md font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30"
                >
                  ⚠️ Pendente ({{ enrollment.completed_sessions }}/{{ enrollment.total_sessions }})
                </span>
                <span
                  v-else-if="enrollment.current_week_attended"
                  class="text-[9px] px-2 py-0.5 rounded-md font-medium bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                >
                  ✓ Presente ({{ enrollment.completed_sessions }}/{{ enrollment.total_sessions }})
                </span>
                <span
                  v-else
                  class="text-[9px] px-2 py-0.5 rounded-md font-medium bg-purple-500/10 text-purple-600 dark:text-purple-300 border border-purple-500/20"
                >
                  📦 {{ enrollment.completed_sessions }}/{{ enrollment.total_sessions }}
                </span>
              </div>
            </div>
          </div>

          <!-- Rescheduled / Encaixes -->
          <div
            v-for="reschedule in selectedSlotForModal.slotData.reschedulings"
            :key="`modal-reschedule-${reschedule.id}`"
            class="p-2.5 rounded-lg text-xs shadow-xs border border-indigo-500/30 bg-indigo-500/10 relative overflow-hidden transition-all cursor-pointer hover:border-indigo-400"
            @click="openEnrollmentFromModal({ id: reschedule.enrollment_id })"
          >
            <div class="flex items-center justify-between">
              <span class="font-medium text-indigo-700 dark:text-indigo-200">
                {{ reschedule.patient?.name }}
              </span>
              <span class="text-[9px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-500/40">
                🔄 Reposição
              </span>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="px-6 py-3.5 border-t border-n-weak bg-n-alpha-1 flex items-center justify-between">
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs cursor-pointer transition-colors"
            @click="createEnrollmentFromModal(null)"
          >
            + Nova Matrícula neste Horário
          </button>

          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors"
            @click="showSlotStudentsModal = false"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL GLOBAL DE RESOLUÇÃO RÁPIDA DE PRESENÇAS PENDENTES -->
    <div
      v-if="showPendingPresencesModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showPendingPresencesModal = false"
    >
      <div class="w-full max-w-3xl bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 flex flex-col max-h-[88vh]">
        <!-- Header Amplo -->
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-lg text-amber-600 dark:text-amber-400">
              ⚠️
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-n-slate-12 leading-tight">
                  Presenças Anteriores Pendentes
                </h3>
                <span class="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                  {{ globalPastPendingList.length }} pendência(s)
                </span>
              </div>
              <p class="text-xs text-n-slate-11 mt-0.5">
                Atendimentos passados aguardando confirmação de presença, reagendamento ou falta.
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <!-- Botão Confirmar Todos -->
            <button
              v-if="globalPastPendingList.length > 1"
              type="button"
              class="h-8 px-3 text-xs font-medium text-emerald-700 hover:text-white dark:text-emerald-300 bg-emerald-500/15 hover:bg-emerald-600 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer shadow-xs flex items-center gap-1.5 disabled:opacity-50"
              :disabled="isResolvingPending"
              @click="confirmAllPastPending"
            >
              <span v-if="!isResolvingPending">✓</span>
              <span v-else class="animate-spin text-xs">⏳</span>
              <span>Confirmar Todos</span>
            </button>

            <button
              type="button"
              class="w-7 h-7 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 flex items-center justify-center cursor-pointer transition-all text-xs"
              @click="showPendingPresencesModal = false"
            >
              ✕
            </button>
          </div>
        </div>

        <!-- Lista de Pendências Agrupada por Data -->
        <div class="p-6 overflow-y-auto space-y-5 flex-1 divide-y divide-n-weak">
          <div
            v-for="group in groupedPastPendingList"
            :key="group.date"
            class="pt-4 first:pt-0 space-y-3"
          >
            <!-- Separador de Data -->
            <div class="flex items-center gap-2 text-xs font-semibold text-n-slate-12 uppercase tracking-wider">
              <span>📅</span>
              <span>{{ group.date }}</span>
              <span class="text-n-slate-10 font-normal">({{ group.items.length }} atendimento{{ group.items.length > 1 ? 's' : '' }})</span>
            </div>

            <!-- Cards dos Pacientes desta Data -->
            <div class="space-y-2.5">
              <div
                v-for="item in group.items"
                :key="item.id"
                class="p-3 rounded-lg bg-n-surface-1 border border-n-weak hover:border-brand transition-all shadow-xs"
              >
                <!-- Linha Principal: Dados à Esquerda & Ações à Direita -->
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <!-- Lado Esquerdo: Paciente & Detalhes -->
                  <div class="space-y-1">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="font-semibold text-n-slate-12 text-sm">
                        {{ item.patient_name }}
                      </span>
                      <span
                        class="text-[11px] px-2.5 py-0.5 rounded-full font-medium bg-n-surface-1 border"
                        :style="{
                          borderColor: item.service_color ? `${item.service_color}60` : 'var(--brand-color, #1f93ff)',
                          color: item.service_color || 'var(--brand-color, #1f93ff)'
                        }"
                      >
                        {{ item.service_name }}
                      </span>
                    </div>
                    <div class="text-xs text-n-slate-11 flex items-center gap-3">
                      <span class="font-mono text-n-slate-12 font-medium">⏰ {{ item.time }}</span>
                      <span>•</span>
                      <span>👤 Prof: {{ item.professional_name || 'Geral' }}</span>
                    </div>
                  </div>

                  <!-- Lado Direito: Ações Rápidas -->
                  <div class="flex items-center gap-2 shrink-0">
                    <!-- Botão Presente -->
                    <button
                      type="button"
                      class="h-7 px-2.5 text-xs font-medium text-emerald-700 hover:text-white dark:text-emerald-300 bg-emerald-500/15 hover:bg-emerald-600 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer shadow-xs flex items-center gap-1 disabled:opacity-50"
                      :disabled="isResolvingPending"
                      title="Marcar presença e concluir atendimento"
                      @click="quickResolvePastPending(item)"
                    >
                      <span>✓</span>
                      <span>Presente</span>
                    </button>

                    <!-- Botão Reagendar -->
                    <button
                      type="button"
                      class="h-7 px-2.5 text-xs font-medium text-n-brand hover:text-white bg-brand-subtle hover:bg-brand border border-brand/30 hover:border-brand rounded-lg transition-all cursor-pointer shadow-xs flex items-center gap-1 disabled:opacity-50"
                      :disabled="isResolvingPending"
                      title="Reagendar este atendimento para outra data/horário"
                      @click="openRescheduleModal(item)"
                    >
                      <span>🔄</span>
                      <span>Reagendar</span>
                    </button>

                    <!-- Botão Falta -->
                    <button
                      type="button"
                      class="h-7 px-2 text-xs font-medium text-rose-600 hover:text-white dark:text-rose-400 bg-rose-500/15 hover:bg-rose-600 border border-rose-500/30 hover:border-rose-500 rounded-lg transition-all cursor-pointer shadow-xs flex items-center gap-1 disabled:opacity-50"
                      :disabled="isResolvingPending"
                      title="Registrar falta do paciente"
                      @click="markPendingAbsent(item)"
                    >
                      <span>✕</span>
                      <span>Falta</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-3.5 border-t border-n-weak bg-n-alpha-1 flex items-center justify-between text-xs text-n-slate-11">
          <span>Total: {{ globalPastPendingList.length }} pendência(s) encontrada(s)</span>
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer bg-n-surface-1 border border-n-weak hover:border-brand rounded-lg transition-all"
            @click="showPendingPresencesModal = false"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL DEDICADO E AMPLO DE REAGENDAMENTO INTELIGENTE (HEATMAP DE VAGAS) -->
    <div
      v-if="showRescheduleModal && selectedPendingForReschedule"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="closeRescheduleModal"
    >
      <div class="w-full max-w-3xl bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 flex flex-col max-h-[90vh]">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12 leading-tight flex items-center gap-2">
              <span>🔄</span> Reagendar Atendimento
            </h3>
            <p class="text-xs text-n-slate-11 mt-1">
              Paciente: <strong class="text-n-slate-12">{{ selectedPendingForReschedule.patient_name }}</strong>
              <span class="mx-1.5 text-n-weak">•</span>
              Serviço: <span class="font-medium" :style="{ color: selectedPendingForReschedule.service_color || 'var(--brand-color, #1f93ff)' }">{{ selectedPendingForReschedule.service_name }}</span>
            </p>
          </div>
          <button
            type="button"
            class="w-7 h-7 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 flex items-center justify-center cursor-pointer transition-all text-xs"
            @click="closeRescheduleModal"
          >
            ✕
          </button>
        </div>

        <!-- Seletor de Profissional & Legenda Limpa -->
        <div class="px-6 py-3 bg-n-surface-1 border-b border-n-weak flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-2.5">
            <span class="text-xs font-semibold text-n-slate-12">Profissional:</span>
            <select
              v-model="rescheduleForm.professionalId"
              class="h-8 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak text-xs text-n-slate-12 font-medium rounded-lg px-3 focus:border-brand outline-none cursor-pointer"
              @change="loadMonthOverview"
            >
              <option
                v-for="p in (props.professionals || filteredProfessionals)"
                :key="p.id"
                :value="p.id"
              >
                {{ p.name }} ({{ p.specialty || 'Profissional' }})
              </option>
            </select>
          </div>

          <!-- Legenda de Cores Limpa (Sem Emojis, Pura Tipografia) -->
          <div class="flex items-center gap-4 text-xs font-medium">
            <span class="text-n-slate-12 flex items-center gap-1.5"><span class="w-2 h-2 rounded-full bg-n-slate-8"></span> Disponível</span>
            <span class="font-medium flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><span class="w-2 h-2 rounded-full bg-amber-400"></span> Vagas parciais</span>
            <span class="font-medium line-through flex items-center gap-1.5 text-rose-600 dark:text-rose-400"><span class="w-2 h-2 rounded-full bg-rose-400"></span> Lotado / Bloqueado</span>
          </div>
        </div>

        <!-- Conteúdo em 2 Colunas Espaçosas -->
        <div class="p-6 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-12 gap-6">
          <!-- Coluna 1: Mini Calendário de Dias (Largura Ampla) -->
          <div class="md:col-span-6 bg-n-alpha-1/50 p-4 rounded-xl border border-n-weak space-y-3">
            <!-- Navegação do Mês -->
            <div class="flex items-center justify-between px-1">
              <span class="text-xs font-bold uppercase tracking-wider text-n-slate-12">
                {{ currentMonthLabel }}
              </span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="w-7 h-7 rounded-lg bg-n-surface-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 flex items-center justify-center text-xs transition-all cursor-pointer"
                  @click="prevMonth"
                >
                  ◀
                </button>
                <button
                  type="button"
                  class="w-7 h-7 rounded-lg bg-n-surface-1 hover:bg-n-alpha-2 border border-n-weak text-n-slate-11 hover:text-n-slate-12 flex items-center justify-center text-xs transition-all cursor-pointer"
                  @click="nextMonth"
                >
                  ▶
                </button>
              </div>
            </div>

            <!-- Dias da Semana Header -->
            <div class="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-n-slate-10 uppercase py-1 border-b border-n-weak">
              <span>Seg</span>
              <span>Ter</span>
              <span>Qua</span>
              <span>Qui</span>
              <span>Sex</span>
              <span>Sáb</span>
              <span>Dom</span>
            </div>

            <!-- Loading Spinner -->
            <div v-if="isLoadingMonthOverview" class="py-14 flex justify-center items-center">
              <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
            </div>

            <!-- Grid de Dias -->
            <div v-else class="grid grid-cols-7 gap-1.5">
              <template v-for="(cell, cIdx) in calendarDaysGrid" :key="cIdx">
                <div v-if="cell.isBlank" class="h-9"></div>
                <button
                  v-else
                  type="button"
                  class="h-9 rounded-lg text-xs flex items-center justify-center transition-all select-none"
                  :class="[
                    cell.isSelected
                      ? 'bg-brand text-white font-bold shadow-xs ring-2 ring-brand/40 scale-105'
                      : cell.isPast || cell.isOff
                        ? 'text-n-slate-9 opacity-25 cursor-not-allowed bg-transparent'
                        : cell.isBlocked || cell.isFull
                          ? 'font-medium opacity-80 line-through bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 cursor-not-allowed'
                          : cell.status === 'partial'
                            ? 'font-semibold bg-amber-500/10 border border-amber-500/30 hover:border-amber-400 hover:bg-amber-500/20 text-amber-700 dark:text-amber-400 cursor-pointer'
                            : 'text-n-slate-12 font-medium bg-n-surface-1 border border-n-weak hover:border-brand hover:bg-n-alpha-2 cursor-pointer'
                  ]"
                  :disabled="!cell.isClickable"
                  :title="cell.isPast ? 'Data passada' : cell.isOff ? 'Sem atendimento' : cell.isFull ? 'Dia lotado' : cell.status === 'partial' ? `Vagas parciais (${cell.availableSlots} livres)` : 'Dia disponível'"
                  @click="onSelectCalendarDay(cell)"
                >
                  {{ cell.dayNum }}
                </button>
              </template>
            </div>
          </div>

          <!-- Coluna 2: Horários do Dia Selecionado -->
          <div class="md:col-span-6 bg-n-alpha-1/50 p-4 rounded-xl border border-n-weak space-y-3 flex flex-col justify-between">
            <div class="space-y-3">
              <div class="flex items-center justify-between pb-2 border-b border-n-weak">
                <span class="text-xs font-semibold text-n-slate-12">
                  Horários em <strong class="text-n-brand font-mono">{{ rescheduleForm.targetDate }}</strong>:
                </span>
                <span class="text-xs font-medium text-n-slate-11">
                  {{ selectedDaySlots.filter(s => s.available).length }} vaga(s) livre(s)
                </span>
              </div>

              <!-- Lista de Pílulas de Horários -->
              <div v-if="selectedDaySlots.length === 0" class="py-12 text-center text-xs text-n-slate-10">
                Nenhum horário disponível para esta data.
              </div>

              <div v-else class="grid grid-cols-2 gap-2 max-h-[230px] overflow-y-auto pr-1">
                <button
                  v-for="slot in selectedDaySlots"
                  :key="slot.time_label"
                  type="button"
                  class="p-2.5 rounded-lg text-xs font-mono transition-all flex items-center justify-between border cursor-pointer"
                  :class="[
                    rescheduleForm.targetTime === slot.time_label && slot.available
                      ? 'bg-brand text-white font-bold border-brand shadow-xs ring-2 ring-brand/30'
                      : !slot.available || slot.status === 'full' || slot.blocked
                        ? 'bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400 opacity-70 cursor-not-allowed line-through'
                        : slot.booked > 0
                          ? 'bg-n-surface-1 hover:bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400 font-semibold'
                          : 'bg-n-surface-1 hover:bg-n-alpha-2 border-n-weak hover:border-brand text-n-slate-12'
                  ]"
                  :disabled="!slot.available"
                  @click="rescheduleForm.targetTime = slot.time_label"
                >
                  <span class="font-bold">{{ slot.time_label }}</span>
                  <span
                    v-if="slot.status === 'full' || !slot.available"
                    class="text-[10px] font-sans font-normal"
                  >
                    (Lotado)
                  </span>
                  <span
                    v-else-if="slot.blocked"
                    class="text-[10px] font-sans font-normal"
                  >
                    (Bloqueio)
                  </span>
                  <span
                    v-else
                    class="text-[10px] font-sans"
                    :class="rescheduleForm.targetTime === slot.time_label ? 'text-white' : 'text-n-slate-11'"
                  >
                    ({{ slot.free_slots }} vaga{{ slot.free_slots > 1 ? 's' : '' }})
                  </span>
                </button>
              </div>
            </div>

            <!-- Resumo e Ação de Confirmar -->
            <div class="pt-3 border-t border-n-weak flex items-center justify-between">
              <div class="text-xs text-n-slate-11">
                Novo horário: <strong class="text-n-brand font-mono">{{ rescheduleForm.targetDate }} às {{ rescheduleForm.targetTime }}</strong>
              </div>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="h-8 px-3 text-xs text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors"
                  @click="closeRescheduleModal"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  class="h-8 px-3.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg transition-all cursor-pointer shadow-xs flex items-center gap-1.5 disabled:opacity-50"
                  :disabled="isResolvingPending"
                  @click="confirmReschedulePending(null)"
                >
                  <span v-if="!isResolvingPending">✓ Confirmar</span>
                  <span v-else>Salvando...</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
